import { useNavigate } from 'react-router-dom';
import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { HiTrash, HiArrowDownTray, HiEye, HiArrowPath } from 'react-icons/hi2';
import { FaGoogleDrive } from 'react-icons/fa';
import { TbBrandOnedrive } from 'react-icons/tb';
import type { DocumentDto } from '@/types';
import { DocumentStatus, ImportSource } from '@/types';
import { apiDownload } from '@/api/client';
import { useDateFormat } from '@/utils/dates';
import DocumentStatusBadge from './DocumentStatusBadge';
import {
  FileIcon,
  DocumentName,
  CellText,
  SummaryText,
  Summarizing,
  LabelBadge,
  LabelsRow,
  MobileCard,
  MobileCardHeader,
  MobileCardBody,
  MobileCardActions,
  NameCell,
  NameContainer,
  ImportSourceIcon,
} from './DocumentList.styled';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface DocumentTableRowProps {
  doc: DocumentDto;
  isSelected: boolean;
  isDeleting: boolean;
  onToggleSelect: (id: string) => void;
  onDelete: (id: string, fileName: string) => void;
  onRetry: (id: string) => void;
}

export default function DocumentTableRow({
  doc,
  isSelected,
  isDeleting,
  onToggleSelect,
  onDelete,
  onRetry,
}: DocumentTableRowProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formatDateTime } = useDateFormat();

  return (
    <tr>
      <td>
        <Form.Check
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(doc.id)}
        />
      </td>
      <td>
        <FileIcon $type={doc.documentType.toLowerCase()}>
          {doc.documentType.toUpperCase().slice(0, 3)}
        </FileIcon>
      </td>
      <NameCell>
        <DocumentName onClick={() => navigate(`/documents/${doc.id}`)}>
          {doc.importSource === ImportSource.GoogleDrive && (
            <ImportSourceIcon><FaGoogleDrive size="0.75rem" /></ImportSourceIcon>
          )}
          {doc.importSource === ImportSource.OneDrive && (
            <ImportSourceIcon><TbBrandOnedrive size="0.75rem" /></ImportSourceIcon>
          )}
          {doc.documentName ?? doc.fileName}
        </DocumentName>
        {doc.summary && (
          <SummaryText>{doc.summary}</SummaryText>
        )}
        {!doc.summary && doc.status === DocumentStatus.Processing && doc.processingProgress >= 90 && (
          <Summarizing>{t('documents.generatingSummary')}</Summarizing>
        )}
        {doc.labels?.length > 0 && (
          <LabelsRow>
            {doc.labels.map((label) => (
              <LabelBadge key={label.id}>{label.name}</LabelBadge>
            ))}
          </LabelsRow>
        )}
      </NameCell>
      <td>
        <DocumentStatusBadge status={doc.status} processingProgress={doc.processingProgress} showProgress />
      </td>
      <CellText>
        {formatFileSize(doc.fileSizeBytes)}
      </CellText>
      <CellText>
        {doc.pageCount ?? '-'}
      </CellText>
      <CellText>
        {formatDateTime(doc.createdAt)}
      </CellText>
      <td>
        <div className="d-flex gap-1">
          {doc.status === DocumentStatus.Failed && (
            <Button
              variant="outline-warning"
              size="sm"
              onClick={() => onRetry(doc.id)}
              title={t('documents.retry')}
            >
              <HiArrowPath size="0.875rem" />
            </Button>
          )}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => navigate(`/documents/${doc.id}`)}
            title={t('documents.preview.title', 'Preview')}
          >
            <HiEye size="0.875rem" />
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => apiDownload(`/documents/${doc.id}/download`, doc.fileName)}
            title={t('documents.download', 'Download')}
          >
            <HiArrowDownTray size="0.875rem" />
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => onDelete(doc.id, doc.fileName)}
            disabled={isDeleting}
            title={t('documents.deleteTitle')}
          >
            <HiTrash size="0.875rem" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

export function DocumentMobileCardItem({
  doc,
  isDeleting,
  onDelete,
  onRetry,
}: Omit<DocumentTableRowProps, 'isSelected' | 'onToggleSelect'>) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formatDateTime } = useDateFormat();

  return (
    <MobileCard>
      <MobileCardHeader>
        <FileIcon $type={doc.documentType.toLowerCase()}>
          {doc.documentType.toUpperCase().slice(0, 3)}
        </FileIcon>
        <NameContainer>
          <DocumentName onClick={() => navigate(`/documents/${doc.id}`)}>
            {doc.importSource === ImportSource.GoogleDrive && (
              <ImportSourceIcon><FaGoogleDrive size="0.75rem" /></ImportSourceIcon>
            )}
            {doc.importSource === ImportSource.OneDrive && (
              <ImportSourceIcon><TbBrandOnedrive size="0.75rem" /></ImportSourceIcon>
            )}
            {doc.documentName ?? doc.fileName}
          </DocumentName>
          {doc.summary && <SummaryText>{doc.summary}</SummaryText>}
          {!doc.summary && doc.status === DocumentStatus.Processing && doc.processingProgress >= 90 && (
            <Summarizing>{t('documents.generatingSummary')}</Summarizing>
          )}
          {doc.labels?.length > 0 && (
            <LabelsRow>
              {doc.labels.map((label) => (
                <LabelBadge key={label.id}>{label.name}</LabelBadge>
              ))}
            </LabelsRow>
          )}
        </NameContainer>
      </MobileCardHeader>
      <MobileCardBody>
        <DocumentStatusBadge status={doc.status} processingProgress={doc.processingProgress} showProgress />
        <span>{formatFileSize(doc.fileSizeBytes)}</span>
        {doc.pageCount != null && <span>{doc.pageCount} {t('documents.table.pages')}</span>}
        <span>{formatDateTime(doc.createdAt)}</span>
      </MobileCardBody>
      <MobileCardActions>
        {doc.status === DocumentStatus.Failed && (
          <Button variant="outline-warning" size="sm" onClick={() => onRetry(doc.id)}>
            <HiArrowPath size="0.875rem" />
          </Button>
        )}
        <Button variant="outline-primary" size="sm" onClick={() => navigate(`/documents/${doc.id}`)}>
          <HiEye size="0.875rem" />
        </Button>
        <Button variant="outline-secondary" size="sm" onClick={() => apiDownload(`/documents/${doc.id}/download`, doc.fileName)}>
          <HiArrowDownTray size="0.875rem" />
        </Button>
        <Button variant="outline-danger" size="sm" onClick={() => onDelete(doc.id, doc.fileName)} disabled={isDeleting}>
          <HiTrash size="0.875rem" />
        </Button>
      </MobileCardActions>
    </MobileCard>
  );
}
